<script>
import { Search } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import Button from "@/shared/ui/button/Button.vue";
import { bulkAssignUsersOrganization, UserCard } from "@/entities/user";
import { deleteNotary, getNotariesList } from "@/entities/session/api/notaries";
import { PROFILE_DRAWER_KEY } from "@/widgets/profile-drawer";
import { FormSheet } from "@/shared/ui/form-sheet";
import { UserAvatar } from "@/shared/ui/user-avatar";
import dateFormat from "@/shared/lib/date-format";
import TableRowActions from "@/shared/ui/custom/components/TableRowActions.vue";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { Tree } from "@/shared/ui/tree";
import { Checkbox } from "@/shared/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
export default {
  name: "NotariesList",
  components: {
    TableFooterWrapper,
    Search,
    Input,
    Button,
    TableRowActions,
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow,
    UserCard,
    UserAvatar,
    Tree,
    Checkbox,
    Popover,
    PopoverTrigger,
    PopoverContent,
    FormSheet,
  },
  inject: {
    profileDrawer: { from: PROFILE_DRAWER_KEY, default: null },
  },
  props: {
    organizations: {
      type: Array,
      default: () => [],
    },
    roles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,

      filters: {
        q: "",
        page: 1,
        page_size: 10,
        organization_id: null,
      },

      selectedIds: [],

      moveDrawerOpen: false,
      moveTargetOrgId: null,
      moveLoading: false,

      pageSizes: [1, 5, 10, 20],
    };
  },
  computed: {
    hasAccess() {
      return this.$store.getters.has_access;
    },
    isAllSelected() {
      return (
        this.list.length > 0 &&
        this.list.every((row) => this.selectedIds.includes(row.uuid))
      );
    },
    isIndeterminate() {
      const count = this.list.filter((row) =>
        this.selectedIds.includes(row.uuid)
      ).length;
      return count > 0 && count < this.list.length;
    },
    organizationTreeItems() {
      const list = Array.isArray(this.organizations) ? this.organizations : [];
      return list.map((org) => ({
        id: org.id,
        label:
          org.short_name ||
          org.short_name_ru ||
          org.name ||
          org.name_ru ||
          this.$t("Без названия"),
        raw: org,
      }));
    },
    actions() {
      const list = [];
      if (this.$store.getters.has_access("ADU")) {
        list.push({
          label: this.$t("Заблокировать"),
          type: "danger",
          action: "block",
          icon: "huge-close",
        });
      }
      if (this.$store.getters.has_access("AUU")) {
        list.push({
          label: this.$t("Редактировать"),
          type: "default",
          action: "edit",
          icon: "huge-pen2",
        });
      }
      if (this.$store.getters.has_access("ADU")) {
        list.push({
          label: this.$t("Удалить"),
          type: "danger",
          action: "remove",
          icon: "huge-danger",
        });
      }
      list.push({
        label: this.$t("Просмотр нотариуса"),
        type: "default",
        action: "view",
        icon: "huge-eye",
        isLast: true,
      });
      return list;
    },
  },
  watch: {
    filters: {
      handler() {
        this.fetchList();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    async fetchList() {
      let params = {
        page_size: this.filters.page_size,
        page: this.filters.page,
      };
      if (this.filters.q) {
        params.q = this.filters.q.trim().toLowerCase();
      }
      if (this.filters.organization_id) {
        params.organization_id = this.filters.organization_id;
      }
      this.loading = true;
      try {
        let data = await getNotariesList(params);
        if (data?.success) {
          if (data.payload) {
            this.total = data.payload?.total;
            this.filters.page_size = data.payload?.page_size;
            this.filters.page = data.payload?.page;
            this.list = data.payload.items || [];
          }
        }
      } finally {
        this.loading = false;
      }
    },
    getRowOrganizationId(row) {
      return (
        row?.organization_id ??
        row?.organization?.id ??
        row?.user?.organization_id ??
        row?.user?.organization?.id ??
        row?.profile?.organization_id ??
        null
      );
    },
    getRowOrganizationDisplay(row) {
      const id = this.getRowOrganizationId(row);
      if (id && this.organizations?.length) {
        const name = this.getOrganizationName(id);
        if (name && name !== this.$t("Не указано")) return name;
      }
      return (
        row?.organization?.name ||
        row?.organization?.short_name ||
        row?.user?.organization?.name ||
        row?.user?.organization?.short_name ||
        this.$t("Не указано")
      );
    },
    getRowRoleCodes(row) {
      if (Array.isArray(row?.role_codes) && row.role_codes.length) {
        return row.role_codes;
      }
      if (Array.isArray(row?.user?.role_codes) && row.user.role_codes.length) {
        return row.user.role_codes;
      }
      if (Array.isArray(row?.roles)) {
        return row.roles.map((r) => r?.code).filter(Boolean);
      }
      return [];
    },

    handlePageChange(page) {
      this.filters.page = page;
    },
    goToCreate() {
      this.profileDrawer?.openCreate?.();
    },
    dateFormat(date) {
      if (!date) {
        return this.$t("Не указано");
      }
      return dateFormat.timestamp_to_local_tz(date, "DD MMM YYYY, HH:mm");
    },
    async handleActions(item) {
      await this.$nextTick();
      if (item.action === "edit") {
        this.profileDrawer?.openEdit?.(item.uuid);
      } else if (item.action === "view") {
        this.goToNotary(item.uuid);
      } else if (item.action === "remove") {
        await this.removeNotary(item.uuid, item.rowData);
      }
    },
    goToNotary(uuid) {
      if (!uuid) return;
      this.profileDrawer?.openView?.(uuid);
    },
    async removeNotary(id, row = null) {
      const fullName = row
        ? [row.last_name, row.first_name].filter(Boolean).join(" ")
        : "";
      const message = fullName
        ? this.$t("Удалить нотариуса {name}? Это действие необратимо.", {
            name: fullName,
          })
        : this.$t("Удалить нотариуса? Это действие необратимо.");
      try {
        await this.$confirm(message, this.$t("Удаление нотариуса"), {
          confirmButtonText: this.$t("Удалить"),
          cancelButtonText: this.$t("Отмена"),
          type: "danger",
        });
      } catch {
        return;
      }
      const data = await deleteNotary(id);
      if (data?.success !== false) {
        await this.fetchList();
      }
    },
    getOrganization(id) {
      let organization = this.organizations.find((org) => org.id === id);
      return organization;
    },
    getOrganizationShortName(id) {
      let organization = this.getOrganization(id);
      return organization?.short_name || organization?.short_name_ru;
    },
    getOrganizationName(id) {
      let organization = this.getOrganization(id);
      return organization?.name || this.$t("Не указано");
    },
    getRoleName(code) {
      if (this.roles && this.roles.length) {
        let role = this.roles.find((role) => role.code === code);
        return role?.name || code;
      }
      return code;
    },
    handleRowClick(row) {
      this.goToNotary(row.uuid);
    },
    search() {
      this.filters.page = 1;
    },
    handleOrganizationSelect(node) {
      this.filters.organization_id = node?.id ?? null;
      this.filters.page = 1;
    },
    isRowSelected(row) {
      return this.selectedIds.includes(row.uuid);
    },
    toggleRowSelect(row, checked) {
      if (checked) {
        if (!this.selectedIds.includes(row.uuid)) {
          this.selectedIds = [...this.selectedIds, row.uuid];
        }
      } else {
        this.selectedIds = this.selectedIds.filter((id) => id !== row.uuid);
      }
    },
    openMoveDrawer() {
      this.moveTargetOrgId = null;
      this.moveDrawerOpen = true;
    },
    closeMoveDrawer() {
      this.moveDrawerOpen = false;
      this.moveTargetOrgId = null;
    },
    async confirmMove() {
      if (!this.moveTargetOrgId || !this.selectedIds.length) return;
      this.moveLoading = true;
      try {
        const targetId = this.moveTargetOrgId;
        const data = await bulkAssignUsersOrganization({
          user_uuids: this.selectedIds,
          organization_id: targetId,
        });
        if (data?.success) {
          this.selectedIds = [];
          this.closeMoveDrawer();
          const sameOrg = this.filters.organization_id === targetId;
          this.filters.page = 1;
          this.filters.organization_id = targetId;
          if (sameOrg) {
            await this.fetchList();
          }
        }
      } finally {
        this.moveLoading = false;
      }
    },
    toggleSelectAll(checked) {
      if (checked) {
        const ids = this.list.map((row) => row.uuid);
        const merged = new Set([...this.selectedIds, ...ids]);
        this.selectedIds = Array.from(merged);
      } else {
        const pageIds = new Set(this.list.map((row) => row.uuid));
        this.selectedIds = this.selectedIds.filter((id) => !pageIds.has(id));
      }
    },
  },
};
</script>

<template>
  <div class="wrapper-page">
    <div class="flex flex-col h-full w-full gap-8">
      <div class="flex justify-between gap-5">
        <div class="relative" style="width: 320px">
          <Search
            class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
          />
          <Input
            v-model="filters.q"
            :placeholder="$t('Поиск по нотариусам')"
            class="pl-9"
            @input="search"
          />
        </div>
        <div class="flex items-center gap-3">
          <Button
            v-if="selectedIds.length"
            variant="outline"
            @click="openMoveDrawer"
          >
            {{ $t("Перенести в папку") }} ({{ selectedIds.length }})
          </Button>
          <Button v-if="hasAccess('ACU')" variant="default" @click="goToCreate"
            >{{ $t("Добавить нотариуса") }} +
          </Button>
        </div>
      </div>

      <div class="content-layout">
        <aside class="content-layout__sidebar">
          <Tree
            v-model="filters.organization_id"
            :items="organizationTreeItems"
            :root-label="$t('Все')"
            @select="handleOrganizationSelect"
          />
        </aside>

        <div class="table-area">
          <div class="h-full overflow-auto">
            <Table :loading="loading" :scroll-reset-key="filters.page">
              <TableHeader>
                <TableRow>
                  <TableHead class="w-[48px]">
                    <div class="flex items-center justify-center" @click.stop>
                      <Checkbox
                        :model-value="isAllSelected"
                        :indeterminate="isIndeterminate"
                        :disabled="!list.length"
                        @update:model-value="toggleSelectAll"
                      />
                    </div>
                  </TableHead>
                  <TableHead class="w-[68px]"></TableHead>
                  <TableHead class="min-w-[320px]">{{
                    $t("Нотариус")
                  }}</TableHead>
                  <TableHead class="min-w-[334px]">{{
                    $t("Организация")
                  }}</TableHead>
                  <TableHead class="min-w-[334px]">{{
                    $t("Роль в системе")
                  }}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableEmpty v-if="!loading && !list.length" :colspan="5">
                  {{ $t("Нет данных") }}
                </TableEmpty>
                <TableRow
                  v-for="row in list"
                  :key="row.uuid"
                  class="cursor-pointer"
                  :data-state="isRowSelected(row) ? 'selected' : undefined"
                  @click="handleRowClick(row)"
                >
                  <TableCell @click.stop>
                    <div class="flex items-center justify-center">
                      <Checkbox
                        :model-value="isRowSelected(row)"
                        @update:model-value="(val) => toggleRowSelect(row, val)"
                      />
                    </div>
                  </TableCell>
                  <TableCell @click.stop>
                    <TableRowActions
                      :actions="actions"
                      @action="
                        (item) =>
                          handleActions({
                            action: item.action,
                            uuid: row.uuid,
                            rowData: row,
                          })
                      "
                    />
                  </TableCell>
                  <TableCell>
                    <UserCard
                      :user-id="row.uuid"
                      side="right"
                      align="start"
                      :side-offset="12"
                      @open-profile="(id) => goToNotary(id)"
                    >
                      <div
                        class="group flex items-center gap-2 cursor-pointer min-w-0"
                      >
                        <UserAvatar
                          :identifier="row.identifier"
                          :seed="row.uuid"
                          :name="row.first_name + ' ' + row.last_name"
                          :size="30"
                        />
                        <div class="flex flex-col gap-0.5 min-w-0">
                          <div
                            class="text-foreground text-sm font-medium truncate transition-colors hover:text-blue-500"
                          >
                            {{ row.first_name + " " + row.last_name }}
                          </div>
                          <div class="text-xs text-muted-foreground truncate">
                            {{ row.identifier }}
                          </div>
                        </div>
                      </div>
                    </UserCard>
                  </TableCell>
                  <TableCell>
                    <div class="flex items-center">
                      {{ getRowOrganizationDisplay(row) }}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div
                      v-if="getRowRoleCodes(row).length && roles?.length"
                      class="flex items-center gap-2 flex-wrap"
                    >
                      <div class="leading-5">
                        {{ getRoleName(getRowRoleCodes(row)[0]) }}
                      </div>
                      <Popover v-if="getRowRoleCodes(row).length > 1">
                        <PopoverTrigger as-child>
                          <button type="button" class="role-more" @click.stop>
                            +{{ getRowRoleCodes(row).length - 1 }}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          align="start"
                          class="w-56 p-2"
                          @click.stop
                        >
                          <ul class="flex flex-col gap-2">
                            <li
                              v-for="item in getRowRoleCodes(row).slice(1)"
                              :key="item"
                              class="flex flex-col justify-start items-start text-sm leading-5 py-1"
                            >
                              {{ getRoleName(item) }}
                            </li>
                          </ul>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div v-else class="flex items-center">
                      {{ $t("Не указано") }}
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <TableFooterWrapper
            v-model:pageSize="filters.page_size"
            v-model:currentPage="filters.page"
            :total="total"
            :pageSizes="[1, 5, 10, 20]"
            :page-size="filters.page_size"
          >
            <template #pagination>
              <Pagination
                v-if="total > filters.page_size"
                :total="total"
                :items-per-page="filters.page_size"
                :page="filters.page"
                :sibling-count="1"
                :disabled="loading"
                show-edges
                @update:page="handlePageChange"
              >
                <PaginationContent v-slot="{ items }">
                  <PaginationPrevious />
                  <template v-for="(item, index) in items" :key="index">
                    <PaginationItem
                      v-if="item.type === 'page'"
                      :value="item.value"
                      :is-active="item.value === filters.page"
                    />
                    <PaginationEllipsis v-else />
                  </template>
                  <PaginationNext />
                </PaginationContent>
              </Pagination>
            </template>
          </TableFooterWrapper>
        </div>
      </div>
    </div>

    <FormSheet
      v-model:open="moveDrawerOpen"
      :title="$t('Перенести в организацию')"
      :width="480"
      @close="closeMoveDrawer"
    >
      <div class="flex flex-col gap-3">
        <div class="text-sm text-muted-foreground">
          {{ $t("Выбрано нотариусов") }}: {{ selectedIds.length }}
        </div>
        <Tree
          v-model="moveTargetOrgId"
          :items="organizationTreeItems"
          :show-root="false"
          :show-checkbox="false"
        />
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-2 w-full">
          <Button
            variant="outline"
            :disabled="moveLoading"
            @click="closeMoveDrawer"
          >
            {{ $t("Отмена") }}
          </Button>
          <Button
            variant="default"
            :disabled="!moveTargetOrgId || moveLoading"
            @click="confirmMove"
          >
            {{ $t("Сохранить") }}
          </Button>
        </div>
      </template>
    </FormSheet>
  </div>
</template>

<style scoped lang="scss">
.content-layout {
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  &__sidebar {
    width: 320px;
    flex-shrink: 0;
    overflow-y: auto;
    padding: 0 8px 8px 0;
  }

  .table-area {
    flex: 1;
    min-width: 0;
  }
}

.role-more {
  @apply inline-flex items-center justify-center border border-border bg-muted text-muted-foreground rounded-md;
  height: 22px;
  min-width: 28px;
  padding: 0 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  transition: colors 0.15s;

  &:hover {
    @apply text-blue-500 border-blue-200 bg-blue-50;
  }
}

.next {
  @apply gap-4 border border-border;
  display: flex;
  padding: 4px 4px 4px 8px;
  align-items: center;
  justify-content: center;
  height: 32px;
  border-radius: 8px;
  background-color: hsl(var(--muted));
}
.arrow-right {
  @apply border border-border;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px !important;

  height: 24px;
  border-radius: 4px;
  background-color: hsl(var(--card));

  svg {
    width: 12px;
    height: 12px;
  }
}
</style>
